/**
 * Landing page GSAP motion — mirrors pricing craft.
 * Post-hydration only. One-time, low-distance section entrances.
 * Transform / opacity only. Honors prefers-reduced-motion.
 */

'use client'

import React, { useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const EASE_OUT = 'power3.out'

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return true
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function afterHydration(cb: () => void): () => void {
  let cancelled = false
  let raf1 = 0
  let raf2 = 0
  let timer = 0
  raf1 = window.requestAnimationFrame(() => {
    raf2 = window.requestAnimationFrame(() => {
      timer = window.setTimeout(() => {
        if (!cancelled) cb()
      }, 0)
    })
  })
  return () => {
    cancelled = true
    window.cancelAnimationFrame(raf1)
    window.cancelAnimationFrame(raf2)
    window.clearTimeout(timer)
  }
}

/** Play a restrained entrance once; content never reverses back to hidden. */
function bindEntrance(
  trigger: Element,
  build: (tl: gsap.core.Timeline) => void,
  opts?: { start?: string },
) {
  const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } })
  build(tl)

  ScrollTrigger.create({
    trigger,
    start: opts?.start ?? 'top 86%',
    onEnter: () => tl.timeScale(1).play(),
    onEnterBack: () => tl.timeScale(1).play(),
    once: true,
    invalidateOnRefresh: true,
  })

  const rect = trigger.getBoundingClientRect()
  const vh = window.innerHeight || 0
  if (rect.top < vh * 0.86 && rect.bottom > 0) {
    tl.timeScale(1).play(0)
  }

  return tl
}

export function useLandingPageMotion(rootSelector = '.landing-main-home') {
  useEffect(() => {
    const root = document.querySelector<HTMLElement>(rootSelector)
    if (!root) return

    if (prefersReducedMotion()) {
      root.classList.add('lp-motion-ready', 'lp-motion-reduced')
      return
    }

    let ctx: gsap.Context | null = null
    const cancelWait = afterHydration(() => {
      root.classList.add('lp-motion-ready')

      ctx = gsap.context(() => {
        /* ——— Hero dual-plane enter ——— */
        const hero = root.querySelector('#overview') || root.querySelector('.lp-section-hero')
        if (hero) {
          const shell = hero.querySelector('.lp-shell')
          const lede = hero.querySelector('.lp-brand-lede')
          const foot = hero.querySelector('.lp-brand-foot')
          const module = hero.querySelector('.lp-module')
          const bezel = hero.querySelector('.lp-bezel')
          const bodyKids = hero.querySelectorAll('.lp-module-body > *')
          const actions = hero.querySelectorAll('.lp-actions > *')

          const heroTl = gsap.timeline({ defaults: { ease: EASE_OUT }, delay: 0.05 })

          if (shell) {
            gsap.set(shell, { opacity: 0.82, y: 12 })
            heroTl.to(shell, { opacity: 1, y: 0, duration: 0.7 }, 0)
          }

          const valueLines = hero.querySelectorAll('.lp-brand-value-line')
          const railBits = [lede, foot].filter(Boolean) as Element[]
          if (railBits.length) {
            gsap.set(railBits, { opacity: 0.86, y: 10 })
            heroTl.to(
              railBits,
              { opacity: 1, y: 0, duration: 0.5, stagger: 0.08 },
              0.16,
            )
          }
          if (valueLines.length) {
            gsap.set(valueLines, { opacity: 0.86, y: 8 })
            heroTl.to(
              valueLines,
              { opacity: 1, y: 0, duration: 0.42, stagger: 0.06 },
              0.28,
            )
          }
          if (module) {
            gsap.set(module, { opacity: 0.86, y: 14 })
            heroTl.to(module, { opacity: 1, y: 0, duration: 0.6 }, 0.24)
          }
          if (bezel) {
            gsap.set(bezel, { opacity: 0.88, y: -4 })
            heroTl.to(bezel, { opacity: 1, y: 0, duration: 0.4 }, 0.38)
          }
          if (bodyKids.length) {
            gsap.set(bodyKids, { opacity: 0.88, y: 8 })
            heroTl.to(
              bodyKids,
              { opacity: 1, y: 0, duration: 0.45, stagger: 0.07 },
              0.44,
            )
          }
          if (actions.length) {
            gsap.set(actions, { opacity: 0.9, y: 6 })
            heroTl.to(
              actions,
              { opacity: 1, y: 0, duration: 0.4, stagger: 0.06 },
              0.58,
            )
          }

        }

        /* ——— Benefits lattice: enter + leave ——— */
        const workspace = root.querySelector('#workspace')
        if (workspace) {
          const head = workspace.querySelector('.lp-band-head')
          const tiles = workspace.querySelectorAll('.lp-briefing-row')

          if (head) gsap.set(head, { y: 10 })
          if (tiles.length) gsap.set(tiles, { y: 12 })

          bindEntrance(workspace, (tl) => {
            if (head) {
              tl.to(head, { y: 0, duration: 0.42 }, 0)
            }
            if (tiles.length) {
              tl.to(
                tiles,
                {
                  y: 0,
                  duration: 0.44,
                  stagger: { each: 0.07, from: 'start' },
                },
                0.12,
              )
            }
          })
        }

        /* ——— Trust path: enter + leave ——— */
        const access = root.querySelector('#access')
        if (access) {
          const intro = access.querySelector('.lp-path-intro')
          const steps = access.querySelectorAll('.lp-path-step')

          if (intro) gsap.set(intro, { y: 10 })
          if (steps.length) gsap.set(steps, { x: -10 })

          bindEntrance(access, (tl) => {
            if (intro) {
              tl.to(intro, { y: 0, duration: 0.42 }, 0)
            }
            if (steps.length) {
              tl.to(
                steps,
                {
                  x: 0,
                  duration: 0.44,
                  stagger: 0.08,
                },
                0.14,
              )
            }
          })
        }

        /* ——— Surfaces / FAQ: soft enter (content depth; no #plans on home) ——— */
        for (const id of ['#surfaces', '#faq'] as const) {
          const block = root.querySelector(id)
          if (!block) continue
          const head = block.querySelector('.lp-band-head')
          const tiles = block.querySelectorAll('.lp-rail-desk, .lp-faq-item')
          if (head) gsap.set(head, { y: 10 })
          if (tiles.length) gsap.set(tiles, { y: 10 })
          bindEntrance(block, (tl) => {
            if (head) tl.to(head, { y: 0, duration: 0.4 }, 0)
            if (tiles.length) {
              tl.to(
                tiles,
                {
                  y: 0,
                  duration: 0.42,
                  stagger: { each: 0.06, from: 'start' },
                },
                0.1,
              )
            }
          })
        }

        /* ——— Close CTA: Agent emerges into window + copy ——— */
        const close = root.querySelector('#start') || root.querySelector('.lp-section-close')
        if (close) {
          const band = close.querySelector('.lp-close')
          const agentWin = close.querySelector('.lp-close-agent')
          const agentImg = close.querySelector('.lp-close-agent-img')
          const copy = close.querySelector('.lp-close-copy')
          const copyKids = close.querySelectorAll('.lp-close-copy > *')
          const actions = close.querySelectorAll('.lp-close .lp-actions > *')

          if (band) gsap.set(band, { y: 12 })
          if (copy) gsap.set(copy, { y: 8 })
          if (copyKids.length) gsap.set(copyKids, { y: 6 })
          if (actions.length) gsap.set(actions, { y: 4 })
          if (agentWin) gsap.set(agentWin, { scale: 0.97 })
          if (agentImg) {
            gsap.set(agentImg, {
              scale: 0.96,
              y: 8,
              transformOrigin: '50% 60%',
            })
          }

          bindEntrance(
            close,
            (tl) => {
              if (band) {
                tl.to(band, { y: 0, duration: 0.46 }, 0)
              }
              if (agentWin) {
                tl.to(agentWin, { scale: 1, duration: 0.4 }, 0.1)
              }
              if (agentImg) {
                tl.to(
                  agentImg,
                  {
                    scale: 1,
                    y: 0,
                    duration: 0.52,
                    ease: 'power3.out',
                  },
                  0.16,
                )
              }
              if (copy) {
                tl.to(copy, { y: 0, duration: 0.4 }, 0.18)
              }
              if (copyKids.length) {
                tl.to(
                  copyKids,
                  { y: 0, duration: 0.38, stagger: 0.05 },
                  0.28,
                )
              }
              if (actions.length) {
                tl.to(
                  actions,
                  { y: 0, duration: 0.36, stagger: 0.05 },
                  0.42,
                )
              }
            },
            { start: 'top 88%' },
          )
        }

        const refresh = window.setTimeout(() => ScrollTrigger.refresh(), 500)
        return () => window.clearTimeout(refresh)
      }, root)
    })

    return () => {
      cancelWait()
      ctx?.revert()
      root.classList.remove('lp-motion-ready', 'lp-motion-reduced')
    }
  }, [rootSelector])
}

export const LandingPageMotion: React.FC = () => {
  useLandingPageMotion()
  return null
}
